import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectorRef,
    ViewChild,
    AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AyudasService, Ayuda } from '../../services/ayudas.service';
import { catchError, finalize, filter } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { DeleteAyudaDialogComponent } from './delete-ayuda-dialog/delete-ayuda-dialog.component';

@Component({
    selector: 'app-mantenedor-ayudas-page',
    standalone: true,
    imports: [
        CommonModule,
        RouterOutlet,
        MatCardModule,
        MatButtonModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatIconModule,
        MatSnackBarModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './mantenedor-ayudas-page.component.html',
    styleUrl: './mantenedor-ayudas-page.component.scss',
})
export class MantenedorAyudasPageComponent implements OnInit, AfterViewInit, OnDestroy {
    displayedColumns: string[] = [
        'titulo',
        'tipo',
        'urlOrPath',
        'estado',
        'orden',
        'action',
    ];
    dataSource = new MatTableDataSource<Ayuda>([]);
    isLoading = false;
    private routerSubscription?: Subscription;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        private ayudasService: AyudasService,
        private snackBar: MatSnackBar,
        private router: Router,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loadAyudas();
        this.subscribeToRouterEvents();
    }

    ngOnDestroy(): void {
        this.routerSubscription?.unsubscribe();
    }

    private subscribeToRouterEvents(): void {
        this.routerSubscription = this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event) => {
                const url = event.urlAfterRedirects;
                if (url === '/mantenedor-ayudas' || url === '/mantenedor-ayudas/' || url.startsWith('/mantenedor-ayudas?')) {
                    this.loadAyudas();
                }
            });
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (item: Ayuda, property: string): string | number => {
            switch (property) {
                case 'titulo':
                    return item.titulo;
                case 'tipo':
                    return item.tipo;
                case 'estado':
                    return item.activo ? 'activo' : 'inactivo';
                case 'orden':
                    return item.orden;
                default:
                    return (item as unknown as Record<string, string | number>)[property] ?? '';
            }
        };
    }

    loadAyudas(): void {
        this.isLoading = true;
        this.ayudasService
            .getList()
            .pipe(
                catchError(() => {
                    this.snackBar.open('Error al cargar ayudas', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of({ success: false, data: [] });
                }),
                finalize(() => {
                    this.isLoading = false;
                    this.cdr.detectChanges();
                })
            )
            .subscribe((res) => {
                if (res.success && res.data) {
                    this.dataSource.data = res.data;
                }
            });
    }

    isBaseRoute(): boolean {
        const url = this.router.url || '';
        return (
            url === '/mantenedor-ayudas' ||
            url === '/mantenedor-ayudas/' ||
            url.startsWith('/mantenedor-ayudas?')
        );
    }

    isCreateRoute(): boolean {
        return this.router.url.startsWith('/mantenedor-ayudas/create');
    }

    onCreate(): void {
        this.router.navigate(['/mantenedor-ayudas/create']);
    }

    onEdit(ayuda: Ayuda): void {
        this.router.navigate(['/mantenedor-ayudas/edit', ayuda.id]);
    }

    onDelete(ayuda: Ayuda): void {
        const ref = this.dialog.open(DeleteAyudaDialogComponent, {
            width: '400px',
            data: { ayuda },
        });
        ref.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
                this.ayudasService.delete(ayuda.id).subscribe({
                    next: (r) => {
                        if (r.success) {
                            this.snackBar.open('Ayuda eliminada', 'Cerrar', {
                                duration: 3000,
                                panelClass: ['success-snackbar'],
                            });
                            this.loadAyudas();
                        }
                    },
                    error: () => {
                        this.snackBar.open('Error al eliminar', 'Cerrar', {
                            duration: 5000,
                            panelClass: ['error-snackbar'],
                        });
                    },
                });
            }
        });
    }

    onDownload(ayuda: Ayuda): void {
        if (ayuda.tipo !== 'documento') {
            if (ayuda.tipo === 'video') {
                window.open(ayuda.urlOrPath, '_blank');
            }
            return;
        }
        this.ayudasService.downloadFile(ayuda.id).subscribe({
            next: (blob) => {
                const ext = ayuda.tipoDocumento || 'pdf';
                const name = `${ayuda.titulo.replace(/[^a-zA-Z0-9-_]/g, '_')}.${ext}`;
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = name;
                a.click();
                URL.revokeObjectURL(url);
            },
            error: () => {
                this.snackBar.open('Error al descargar el archivo', 'Cerrar', {
                    duration: 5000,
                    panelClass: ['error-snackbar'],
                });
            },
        });
    }

    tipoLabel(tipo: string): string {
        return tipo === 'video' ? 'Video YouTube' : 'Documento';
    }
}
